import { Icon } from '../../components/ui/Icon.js';
import { AuthLayout } from './AuthLayout.js';
import { useEffect, useState, type FormEvent } from 'react';
import { PASSWORD_MIN_LENGTH } from '@arcnet/contracts';
import { ApiClientError, login } from '../../lib/api/client.js';

interface Preview {
  email: string;
  full_name: string | null;
  role_name: string;
  scope_level: string;
  tenant_name: string | null;
  expires_at: string;
}

/**
 * Invitation acceptance (doc 06 section 7).
 *
 * The invitee chooses their own password here. An administrator never sets one
 * on someone else's behalf, so a credential never exists in two places and no
 * admin is ever in a position to know a user's password.
 *
 * Rendered BEFORE the authentication gate: the person using it has no account
 * yet, so there is nothing to authenticate.
 */
export function AcceptInvitePage({ token, onAccepted }: {
  token: string;
  onAccepted: () => Promise<void>;
}) {
  const [preview, setPreview] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const res = await fetch(`/api/v1/invitations/token/${encodeURIComponent(token)}`);
        if (!res.ok) { setInvalid(true); return; }
        const body = (await res.json()) as Preview;
        setPreview(body);
        setFullName(body.full_name ?? '');
      } catch { setInvalid(true); }
      finally { setLoading(false); }
    })();
  }, [token]);

  const tooShort = password.length > 0 && password.length < PASSWORD_MIN_LENGTH;
  const mismatch = confirm.length > 0 && password !== confirm;
  const canSubmit = password.length >= PASSWORD_MIN_LENGTH && password === confirm && !busy;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !preview) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/invitations/token/${encodeURIComponent(token)}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, full_name: fullName || undefined }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new ApiClientError(res.status, body, 'Could not create the account');
      }
      // Sign straight in. Making someone re-type credentials they set two
      // seconds ago is friction with no security value.
      await login(preview.email, password);
      window.history.replaceState({}, '', '/');
      await onAccepted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <AuthLayout>
        <div className="arc-skeleton" style={{ width: 220, height: 12 }} />
      </AuthLayout>
    );
  }

  if (invalid || !preview) {
    return (
      <AuthLayout>
          <div style={{ textAlign: 'center' }}>
            <div className="arc-empty-icon" style={{ margin: '0 auto 10px' }}>
              <Icon name="clock" size={17} />
            </div>
            <h1 className="arc-h2" style={{ marginBottom: 6 }}>This invitation is no longer valid</h1>
            {/* Deliberately does not say WHICH -- expired, already used and
                never-existed are indistinguishable here, so the page cannot be
                used to probe for live invitation tokens. */}
            <p className="arc-muted" style={{ fontSize: 13.5 }}>
              It may have expired, already been used, or been revoked.
              Ask your administrator for a new one.
            </p>
            <a href="/" className="arc-btn" style={{ marginTop: 16 }}>Go to sign in</a>
          </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
        <div style={{ marginBottom: 20 }}>
          <h1 className="arc-h1">Set your password</h1>
          <p className="arc-muted" style={{ marginTop: 4, fontSize: 13 }}>
            Finish setting up your account.
          </p>
        </div>

        <form onSubmit={submit} noValidate>
          <div
            className="arc-banner arc-banner-info"
            style={{ marginBottom: 18, display: 'block' }}
          >
            <div style={{ fontWeight: 600 }}>{preview.email}</div>
            <div style={{ fontSize: 12.5, marginTop: 3 }}>
              {preview.role_name}
              {preview.tenant_name ? ` · ${preview.tenant_name}` : ' · platform-wide'}
            </div>
          </div>

          {error && (
            <div className="arc-banner arc-banner-danger" style={{ marginBottom: 16 }} role="alert">
              <Icon name="warning" size={14} /><span>{error}</span>
            </div>
          )}

          <div className="arc-field">
            <label className="arc-label" htmlFor="inv-name">Your name</label>
            <input
              id="inv-name" className="arc-input" autoComplete="name"
              value={fullName} onChange={(e) => setFullName(e.target.value)}
              placeholder="Tim"
            />
          </div>

          <div className="arc-field">
            <label className="arc-label" htmlFor="inv-pw">Choose a password</label>
            <input
              id="inv-pw" type="password" className="arc-input"
              autoComplete="new-password" required autoFocus
              value={password} onChange={(e) => setPassword(e.target.value)}
              aria-invalid={tooShort}
              aria-describedby="inv-pw-hint"
            />
            <div id="inv-pw-hint" className={tooShort ? 'arc-error' : 'arc-hint'}>
              {/* No composition rules: they push people towards predictable
                  patterns. Length is what actually resists guessing. */}
              At least {PASSWORD_MIN_LENGTH} characters. A passphrase of a few
              words is stronger than a short complicated one.
            </div>
          </div>

          <div className="arc-field">
            <label className="arc-label" htmlFor="inv-pw2">Confirm password</label>
            <input
              id="inv-pw2" type="password" className="arc-input"
              autoComplete="new-password" required
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
              aria-invalid={mismatch}
            />
            {mismatch && <div className="arc-error">Passwords do not match</div>}
          </div>

          <button
            type="submit"
            className="arc-btn arc-btn-primary"
            style={{ width: '100%', marginTop: 4 }}
            disabled={!canSubmit}
          >
            {busy ? 'Creating your account…' : 'Set password and sign in'}
          </button>
        </form>
    </AuthLayout>
  );
}
